import type { Ad } from "@models/Ad.model";
import type { AdStatus } from "@models/adGroup.model";
import AdCard from "@/shared/components/AdCard/AdCard";

type Props = {
  ad: Ad;
  launchAdId: number;
  status: AdStatus;
  isBusy: boolean;
  onOpen: () => void;
  onStatusChange: (next: AdStatus) => void;
  onSubmitForReview: () => void;
  /** Only the ad-group view can unlink; at campaign level there is nothing to unlink from. */
  onUnlink?: () => void;
  /** Rename this campaign's copy of the ad (MPN-1508). Absent where retitling does not apply. */
  onRetitle?: () => void;
  /** Template this ad was rebranded from. Comes off the row, not the ad (MPN-1508). */
  sourceTemplateName?: string | null;
  /** UTM toggle for this ad on this campaign (MPN-1536). Undefined outside a campaign,
      which is what keeps the control out of the Content Catalog. */
  appendUtm?: boolean;
  /** The URL this ad will actually publish with, resolved server-side. */
  destinationUrl?: string | null;
  onToggleUtm?: (next: boolean) => void;
};

const STANDING: Record<AdStatus, string> = {
  TODO: "Not started",
  IN_PROGRESS: "Draft, not submitted",
  IN_REVIEW: "Waiting on the client",
  CHANGES_REQUESTED: "The client asked for changes",
  APPROVED: "Approved",
  DO_NOT_USE: "Rejected — not to be used for this client",
};

const STATUS_OPTIONS: { value: AdStatus; label: string }[] = [
  { value: "TODO", label: "To do" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "IN_REVIEW", label: "In review" },
  { value: "CHANGES_REQUESTED", label: "Changes requested" },
  { value: "APPROVED", label: "Approved" },
  // Selectable so Ops can both apply and lift it -- the ticket is explicit that this is
  // reversible, and a status you can enter but not leave is a trap (MPN-1612).
  { value: "DO_NOT_USE", label: "Do not use" },
];

// The track has four stages — To do, In progress, In review, Approved.
// CHANGES_REQUESTED is not a fifth stage; it loops back into the In review
// slot, so it shares that slot's index but renders red instead of amber.
//
// DO_NOT_USE is not a stage either. It is where a GuidePost leaves the workflow, so it
// sits at the review slot rather than pretending to be progress towards approval
// (MPN-1612).
const TRACK_INDEX: Record<AdStatus, number> = {
  TODO: 0,
  IN_PROGRESS: 1,
  IN_REVIEW: 2,
  CHANGES_REQUESTED: 2,
  APPROVED: 3,
  DO_NOT_USE: 2,
};

function segmentClass(segmentIndex: number, status: AdStatus): string {
  const current = TRACK_INDEX[status];
  if (segmentIndex < current) return "done";
  if (segmentIndex === current) return status === "CHANGES_REQUESTED" ? "bad" : "now";
  return "";
}

export default function AdRow({
  ad,
  status,
  isBusy,
  onOpen,
  onRetitle,
  onStatusChange,
  onSubmitForReview,
  onUnlink,
  sourceTemplateName,
  appendUtm,
  destinationUrl,
  onToggleUtm,
}: Props) {
  return (
    <div className="ad-tile">
      {/*
          The creative is what is being reviewed, so it is shown at card size
          rather than as a 32px chip you had to hover. AdCard's header already
          carries the name and type — repeating them below would just be noise.

          The actions live on right-click and the header menu: "View full" comes
          from AdCard, unlinking the ad from this ad group is ours.

          "Rename for this campaign" is deliberately worded that way (MPN-1508): the ad here is
          this campaign's own copy, so renaming it leaves the template and every other campaign
          alone. A bare "Rename" would read as renaming the template.
      */}
      <AdCard
        ad={ad}
        mode="detail"
        sourceTemplateName={sourceTemplateName}
        contextMenu
        menuActions={[
          { label: "Open", onSelect: onOpen },
          ...(onRetitle
            ? [{ label: "Rename for this campaign", onSelect: onRetitle, disabled: isBusy }]
            : []),
          ...(onUnlink
            ? [{ label: "Unlink ad", onSelect: onUnlink, danger: true, disabled: isBusy }]
            : []),
        ]}
        onClick={onOpen}
      />

      <div className="ad-tile__bar">
        <div className="ad-track">
          {[0, 1, 2, 3].map((segmentIndex) => (
            <i key={segmentIndex} className={segmentClass(segmentIndex, status)} />
          ))}
        </div>

        <div className="ad-tile__row">
          <select
            className="form-select ad-status-select"
            value={status}
            disabled={isBusy}
            aria-label="Review stage"
            onChange={(e) => onStatusChange(e.target.value as AdStatus)}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ad-tile__open"
            onClick={onOpen}
          >
            Open
          </button>
        </div>

        {/*
            The UTM toggle (MPN-1536).

            Rendered only when the caller passes a handler, which is only ever inside a
            campaign. The Content Catalog shows the same AdCard and must not offer this:
            a catalog ad is a template with no client destination URL yet, and the toggle
            belongs to an ad's membership of one campaign rather than to the ad.

            The resolved URL is printed beneath rather than assembled here, so what Ops
            reads is the same string the networks and the client approval view get.
        */}
        {onToggleUtm && (
          <div className="ad-tile__utm">
            <label className="ad-tile__utm-row">
              <input
                type="checkbox"
                checked={appendUtm ?? true}
                disabled={isBusy}
                onChange={(e) => onToggleUtm(e.target.checked)}
              />
              <span>Tag destination URL with <code>utm_source=motimatic</code></span>
            </label>
            {destinationUrl ? (
              <div className="ad-tile__utm-url" title={destinationUrl}>{destinationUrl}</div>
            ) : (
              <div className="ad-tile__utm-url ad-tile__utm-url--empty">
                No destination URL set for this ad yet.
              </div>
            )}
          </div>
        )}

        {/* The standing says whose turn it is; only worth a line of its own
            when it is not already obvious from the stage above. */}
        <div className={`ad-tile__standing ad-tile__standing--${status.toLowerCase()}`}>
          {STANDING[status]}
        </div>

        {status === "IN_PROGRESS" && (
          <button
            type="button"
            className="btn btn-sm btn-primary w-100"
            disabled={isBusy}
            onClick={onSubmitForReview}
          >
            Submit for review
          </button>
        )}
      </div>
    </div>
  );
}
